import React, { useState, useEffect } from 'react';
import { firestore } from '../firebase';
import { getActiveEventId, resolveAdminDefaultEventId } from '../utils/activeEvent';
import {
  collection,
  getDocs,
  getDoc,
  doc,
  query,
  where
} from 'firebase/firestore';
import './TeamExport.css';

const toNumberOrNull = (value) => {
  if (value == null || value === '') return null;
  const n = Number.parseInt(String(value), 10);
  return Number.isFinite(n) ? n : null;
};

const normalizeTeamFields = (team) => {
  const teamNumberRaw = team.teamNumber ?? team.number ?? team.teamNum ?? '';
  const teamNumberParsed = toNumberOrNull(teamNumberRaw);
  const teamNumber = teamNumberParsed ?? String(teamNumberRaw).trim();
  const teamName = String(team.teamName ?? team.name ?? '').trim();
  const passphrase = String(team.passphrase ?? team.passcode ?? '').trim();
  const division = String(team.division ?? '').trim();
  return { teamNumber, teamName, passphrase, division };
};

const TeamExport = () => {
  const [activeEvents, setActiveEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState('');
  const [teams, setTeams] = useState([]);
  const [teamPasscodes, setTeamPasscodes] = useState({}); // { teamNum: passcode }
  const [exportFormat, setExportFormat] = useState('array'); // 'array' | 'object'
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Fetch active events from Firestore
  useEffect(() => {
    let cancelled = false;
    async function fetchEvents() {
      try {
        const [qs, activeId] = await Promise.all([
          getDocs(collection(firestore, 'events')),
          getActiveEventId(),
        ]);
        if (cancelled) return;
        const eventsData = qs.docs.map((d) => ({ id: d.id, ...d.data() }));
        setActiveEvents(eventsData);
        if (eventsData.length > 0) {
          setSelectedEvent(
            resolveAdminDefaultEventId({ eventIdParam: '', activeEventId: activeId, events: eventsData })
          );
        }
      } catch (err) {
        if (!cancelled) setError('Failed to fetch events: ' + err.message);
      }
    }
    fetchEvents();
    return () => {
      cancelled = true;
    };
  }, []);

  // Fetch teams for the selected event
  useEffect(() => {
    async function fetchTeams() {
      if (!selectedEvent) return;
      setLoading(true);
      try {
        const q = query(collection(firestore, 'teams'), where('eventId', '==', selectedEvent));
        const qs = await getDocs(q);
        const teamsData = qs.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        if (teamsData.length > 0) {
          const sample = teamsData[0];
          // Confirms Firestore field names vs export fallbacks (teamNumber|number|teamNum, teamName|name, passcode|passphrase).
          console.info('[TeamExport] Sample team document fields', {
            eventId: selectedEvent,
            teamId: sample.id,
            keys: Object.keys(sample).sort(),
            normalizedForExport: normalizeTeamFields(sample),
            sample,
          });
        }
        // Sort by normalized team number, fallback to 0 for non-numeric values.
        teamsData.sort((a, b) => {
          const numA = toNumberOrNull(a.teamNumber ?? a.number ?? a.teamNum) ?? 0;
          const numB = toNumberOrNull(b.teamNumber ?? b.number ?? b.teamNum) ?? 0;
          return numA - numB;
        });
        setTeams(teamsData);
        setLoading(false);
      } catch (err) {
        setError('Failed to fetch teams: ' + err.message);
        setLoading(false);
      }
    }
    fetchTeams();
  }, [selectedEvent]);

  // Fetch team passcodes for selected event (login source of truth)
  useEffect(() => {
    if (!selectedEvent) return;
    (async () => {
      try {
        const ref = doc(firestore, 'teamPasscodes', selectedEvent);
        const snap = await getDoc(ref);
        const data = snap.exists() ? snap.data() : {};
        const passcodes = data.passcodes && typeof data.passcodes === 'object' ? data.passcodes : {};
        setTeamPasscodes(passcodes);
      } catch (_) {
        setTeamPasscodes({});
      }
    })();
  }, [selectedEvent]);

  // Build passcodes list: teamNumber, passphrase (from teamPasscodes or team.passcode), votes: 0
  const getPasscodesList = () => {
    const byNumber = new Map();
    teams.forEach(team => {
      const normalized = normalizeTeamFields(team);
      const key = String(normalized.teamNumber);
      const passphrase = String(teamPasscodes[key] ?? normalized.passphrase ?? '').trim();
      byNumber.set(key, {
        teamNumber: normalized.teamNumber,
        teamName: normalized.teamName,
        passphrase,
        votes: 0,
        division: normalized.division,
      });
    });
    // Include any passcodes that aren't in teams (e.g. passcode-only entries)
    Object.entries(teamPasscodes).forEach(([key, passphrase]) => {
      const num = toNumberOrNull(key);
      const teamNumber = num ?? String(key).trim();
      const mapKey = String(teamNumber);
      if (!byNumber.has(mapKey)) {
        byNumber.set(mapKey, { teamNumber, teamName: '', passphrase: (passphrase || '').trim(), votes: 0, division: '' });
      }
    });
    return Array.from(byNumber.values()).sort((a, b) => {
      const aNum = toNumberOrNull(a.teamNumber);
      const bNum = toNumberOrNull(b.teamNumber);
      if (aNum != null && bNum != null) return aNum - bNum;
      if (aNum != null) return -1;
      if (bNum != null) return 1;
      return String(a.teamNumber).localeCompare(String(b.teamNumber));
    });
  };

  const handleDownload = () => {
    const list = getPasscodesList();
    if (list.length === 0) {
      alert('No passcodes to download for this event.');
      return;
    }

    const formattedData = exportFormat === 'object'
      ? list.reduce((acc, item) => {
          acc[String(item.teamNumber)] = item;
          return acc;
        }, {})
      : list;

    const jsonString = JSON.stringify(formattedData, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;

    const eventName = activeEvents.find(e => e.id === selectedEvent)?.title || 'passcodes';
    const filename = `${eventName}_passcodes_${new Date().toISOString().split('T')[0]}.json`;

    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="team-export">
      <h2>Export Team Numbers & Passcodes</h2>
      {error && <p className="error">{error}</p>}
      
      <div className="export-container">
        <div className="dropdown-container">
          <label htmlFor="activeEvent">Select Event:</label>
          <select 
            id="activeEvent" 
            value={selectedEvent} 
            onChange={e => {
              setSelectedEvent(e.target.value);
              setLoading(true);
            }}
          >
            {activeEvents.map(event => (
              <option key={event.id} value={event.id}>{event.title}</option>
            ))}
          </select>
        </div>

        {loading ? (
          <p>Loading teams...</p>
        ) : (
          <>
            <div className="teams-info">
              <p>Found <strong>{getPasscodesList().length}</strong> passcode(s) for this event.</p>
            </div>

            <div className="export-format-row">
              <label htmlFor="exportFormat">Export format:</label>
              <select
                id="exportFormat"
                value={exportFormat}
                onChange={(e) => setExportFormat(e.target.value)}
              >
                <option value="array">Array</option>
                <option value="object">Object (keyed by team number)</option>
              </select>
            </div>

            <button 
              className="download-button" 
              onClick={handleDownload}
              disabled={getPasscodesList().length === 0}
            >
              Download passcodes JSON
            </button>

            {getPasscodesList().length > 0 && (
              <div className="preview-container">
                <h3>Preview (first 5):</h3>
                <pre className="json-preview">
                  {(() => {
                    const list = getPasscodesList().slice(0, 5);
                    const preview = exportFormat === 'object'
                      ? list.reduce((acc, item) => {
                          acc[String(item.teamNumber)] = item;
                          return acc;
                        }, {})
                      : list;
                    return JSON.stringify(preview, null, 2);
                  })()}
                  {getPasscodesList().length > 5 && '\n  ...\n  (and ' + (getPasscodesList().length - 5) + ' more)'}
                </pre>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default TeamExport;
